/* eslint-disable no-console */
import Barcode from 'react-barcode';
import RealBarcodeGenerator from './RealBarcodeGenerator';

/**
 * Professional barcode printing utility
 * Handles actual printing instead of simulation
 * kware: تحديث PrintHelper لاستخدام مولد الباركود الحقيقي بدلاً من المحاكاة
 */
class PrintHelper {
  constructor() {
    this.printWindow = null;
    this.barcodeGenerator = new RealBarcodeGenerator(); // kware: إضافة مولد الباركود الحقيقي
  }

  /**
   * Generate printable HTML for barcodes
   */
  generatePrintableHTML(items, settings) {
    const { format, layout, includeText, paperSize, orientation, copies } = settings;

    // CSS styles for different layouts
    const styles = this.getLayoutStyles(layout, paperSize, orientation);

    // Generate barcode HTML for each item
    const barcodesHTML = items.map((item, index) => {
      return this.generateSingleBarcodeHTML(item, settings, index);
    }).join('');

    // Repeat for multiple copies
    let finalHTML = '';
    for (let copy = 1; copy <= copies; copy++) {
      finalHTML += barcodesHTML;
      if (copy < copies) {
        finalHTML += '<div class="page-break"></div>';
      }
    }

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Barcodes</title>
          <style>
            ${styles}
            .page-break {
              page-break-before: always;
            }
            .no-print {
              display: none !important;
            }
            @media print {
              .no-print {
                display: none !important;
              }
            }
          </style>
        </head>
        <body>
          ${finalHTML}
        </body>
      </html>
    `;
  }

  /**
   * Generate HTML for a single barcode
   * kware: استبدال محاكاة الباركود بباركود حقيقي باستخدام JsBarcode
   */
  generateSingleBarcodeHTML(item, settings, index) {
    const { format, layout, includeText } = settings;

    // Generate real barcode using RealBarcodeGenerator
    const barcodeDataURL = this.barcodeGenerator.generateBarcodeDataURL(
      item.barcode,
      format,
      {
        width: 2,
        height: 60,
        displayValue: false, // We'll add text separately
        background: '#ffffff',
        lineColor: '#000000'
      }
    );

    const barcodeHTML = `
      <div class="barcode-container">
        <div class="barcode-item">
          ${barcodeDataURL ?
    `<img src="${barcodeDataURL}" alt="${item.barcode}" style="max-width: 100%; height: auto;" />` :
    '<div class="barcode-error">Error generating barcode</div>'
}
          ${includeText ? `<div class="barcode-text">${item.barcode}</div>` : ''}
        </div>
        <div class="item-info">
          <div class="material-type">${item.materialType?.name || 'N/A'}</div>
          <div class="location">${item.effectiveLocation?.name || 'N/A'}</div>
        </div>
      </div>
    `;

    return barcodeHTML;
  }

  /**
   * Get CSS styles for different layouts
   */
  getLayoutStyles(layout, paperSize, orientation) {
    const baseStyles = `
      body {
        margin: 0;
        padding: 20px;
        font-family: Arial, sans-serif;
        font-size: 12px;
      }
      .barcode-container {
        display: inline-block;
        margin: 10px;
        padding: 10px;
        border: 1px solid #ddd;
        text-align: center;
        vertical-align: top;
      }
      .barcode-item {
        margin-bottom: 5px;
      }
      .barcode-text {
        font-family: monospace;
        font-size: 10px;
        margin-top: 2px;
      }
      .item-info {
        font-size: 9px;
        color: #666;
      }
      .material-type, .location {
        margin: 1px 0;
      }
    `;

    // Layout-specific styles
    const layoutStyles = {
      standard: `
        .barcode-container {
          width: 250px;
          margin: 15px;
        }
      `,
      compact: `
        .barcode-container {
          width: 180px;
          margin: 8px;
        }
        .barcode-item svg {
          width: 150px;
          height: 40px;
        }
      `,
      labels: `
        .barcode-container {
          width: 120px;
          height: 80px;
          margin: 2px;
          padding: 5px;
          font-size: 8px;
        }
        .barcode-item svg {
          width: 100px;
          height: 30px;
        }
      `
    };

    // Paper size styles
    const paperStyles = {
      A4: orientation === 'landscape' ?
        '@page { size: A4 landscape; margin: 1cm; }' :
        '@page { size: A4; margin: 1cm; }',
      Letter: orientation === 'landscape' ?
        '@page { size: 8.5in 11in landscape; margin: 1in; }' :
        '@page { size: 8.5in 11in; margin: 1in; }',
      Legal: orientation === 'landscape' ?
        '@page { size: 8.5in 14in landscape; margin: 1in; }' :
        '@page { size: 8.5in 14in; margin: 1in; }',
      A5: orientation === 'landscape' ?
        '@page { size: A5 landscape; margin: 0.5cm; }' :
        '@page { size: A5; margin: 0.5cm; }'
    };

    return baseStyles + layoutStyles[layout] + paperStyles[paperSize];
  }

  /**
   * Execute the actual print operation
   */
  async executePrint(items, settings) {
    try {
      // Generate printable HTML
      const printHTML = this.generatePrintableHTML(items, settings);

      // Open print window
      this.printWindow = window.open('', '_blank', 'width=800,height=600');

      if (!this.printWindow) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      // Write HTML to print window
      this.printWindow.document.write(printHTML);
      this.printWindow.document.close();

      // Wait for content to load
      await new Promise((resolve) => {
        this.printWindow.onload = resolve;
        setTimeout(resolve, 1000); // Fallback timeout
      });

      // Execute print
      this.printWindow.print();

      // Close window after printing (optional)
      setTimeout(() => {
        if (this.printWindow && !this.printWindow.closed) {
          this.printWindow.close();
        }
      }, 100);

      return {
        success: true,
        message: `Successfully printed ${items.length} barcodes`,
        itemCount: items.length
      };
    } catch (error) {
      console.error('Print error:', error);

      // Close print window if it was opened
      if (this.printWindow && !this.printWindow.closed) {
        this.printWindow.close();
      }

      return {
        success: false,
        message: error.message || 'Failed to print barcodes',
        error
      };
    }
  }

  /**
   * Check if printing is supported
   */
  isPrintingSupported() {
    return typeof window !== 'undefined' &&
           typeof window.print === 'function';
  }

  /**
   * Get print preview
   */
  async generatePreview(items, settings) {
    if (items.length === 0) {
      return null;
    }

    try {
      // Generate HTML for preview (limited to first 5 items)
      const previewItems = items.slice(0, 5);
      const previewHTML = this.generatePrintableHTML(previewItems, {
        ...settings,
        copies: 1 // Always show single copy in preview
      });

      return {
        html: previewHTML,
        itemCount: items.length,
        previewCount: previewItems.length
      };
    } catch (error) {
      console.error('Preview generation error:', error);
      return null;
    }
  }
}

export default PrintHelper;
